export interface QuizData {
  isOwner: string
  state: string
  revenue: string
  profit: string
  locations: string
  providers: string
  payerMix: string
  concentration: string
  trend: string
  timeline: string
  ownerHours: string
}

export interface ValuationResult {
  low: number
  high: number
  confidence: 'Low' | 'Medium' | 'High'
  valueUnlocks: string[]
  qualityScore: number
  multipleRange: [number, number]
  ebitda: number
  keyManRisk: 'High' | 'Moderate' | 'Low'
}

const REVENUE_MIDPOINTS: Record<string, number> = {
  '500K-1M': 750000,
  '1-3M': 2000000,
  '3-5M': 4000000,
  '5-10M': 7500000,
  '10M+': 12000000,
}

const PROFIT_PERCENTAGES: Record<string, number> = {
  '0-10': 0.05,
  '10-15': 0.125,
  '15-25': 0.2,
  '25+': 0.3,
  'not-sure': 0.12,
}

export function calculateValuation(data: QuizData): ValuationResult {
  // Revenue midpoint
  const revenueMidpoint = REVENUE_MIDPOINTS[data.revenue] || 750000

  // EBITDA estimation (no add-backs)
  const profitPct = PROFIT_PERCENTAGES[data.profit] || 0.12
  const ebitda = revenueMidpoint * profitPct

  // VERY CONSERVATIVE MARKET-VALIDATED MULTIPLE CALCULATION
  let baseMultiple = 3.0 // Significantly reduced from 4.0 to reflect real market conditions

  // Key Man Risk Adjustment (MOST IMPORTANT) - Very conservative
  let keyManRisk: 'High' | 'Moderate' | 'Low' = 'Moderate'
  if (data.ownerHours === '40+') {
    baseMultiple -= 1.0 // Increased penalty from 0.75
    keyManRisk = 'High'
  } else if (data.ownerHours === '20-40') {
    baseMultiple -= 0.5 // Increased penalty from 0.25
    keyManRisk = 'Moderate'
  } else if (data.ownerHours === '<20') {
    baseMultiple += 0.3 // Reduced from 0.4
    keyManRisk = 'Low'
  } else if (data.ownerHours === '0') {
    baseMultiple += 0.5 // Reduced from 0.6
    keyManRisk = 'Low'
  }

  // Single location + single provider penalty - Harsher
  if (data.locations === '1' && data.providers === '1-3') {
    baseMultiple -= 0.75 // Increased from 0.5
  }

  // Multi-location bonus - More conservative
  if (data.locations === '2-3') {
    baseMultiple += 0.15 // Reduced from 0.2
  } else if (['4-5', '6+'].includes(data.locations)) {
    baseMultiple += 0.3 // Reduced from 0.4
  }

  // Large provider team bonus - More conservative
  if (data.providers === '8+') {
    baseMultiple += 0.3 // Reduced from 0.4
  }

  // Growth bonus - More conservative
  if (['up-10-20', 'up-20+'].includes(data.trend)) {
    baseMultiple += 0.15 // Reduced from 0.2
  }

  // Diversified payer mix bonus - More conservative
  if (data.payerMix === 'mix') {
    baseMultiple += 0.15 // Reduced from 0.2
  }

  // No concentration risk bonus - More conservative
  if (data.concentration === 'no') {
    baseMultiple += 0.15 // Reduced from 0.2
  }

  // Strong profit margins bonus - More conservative
  if (['15-25', '25+'].includes(data.profit)) {
    baseMultiple += 0.15 // Reduced from 0.2
  }

  // Calculate multiple range (±0.5x from base)
  const lowMultiple = Math.max(2.0, baseMultiple - 0.5) // Floor reduced from 3.0 to 2.0
  const highMultiple = Math.min(5.0, baseMultiple + 0.5) // Cap reduced from 6.5 to 5.0

  // Calculate valuation range
  const low = Math.round(ebitda * lowMultiple)
  const high = Math.round(ebitda * highMultiple)

  // Quality score for legacy compatibility
  let qualityScore = 0
  if (['up-10-20', 'up-20+'].includes(data.trend)) qualityScore++
  if (['2-3', '4-5', '6+'].includes(data.locations)) qualityScore++
  if (['8+'].includes(data.providers)) qualityScore++
  if (data.concentration === 'no') qualityScore++
  if (data.payerMix === 'mix') qualityScore++
  if (['15-25', '25+'].includes(data.profit)) qualityScore++
  if (['<20', '0'].includes(data.ownerHours)) qualityScore++

  // Confidence calculation
  let confidence: 'Low' | 'Medium' | 'High' = 'Medium'
  const hasNotSure = data.profit === 'not-sure'

  if (qualityScore >= 5 && !hasNotSure) confidence = 'High'
  else if (qualityScore <= 2 || hasNotSure) confidence = 'Low'

  // Value unlocks
  const valueUnlocks = generateValueUnlocks(data, keyManRisk, qualityScore)

  return {
    low,
    high,
    confidence,
    valueUnlocks,
    qualityScore,
    multipleRange: [lowMultiple, highMultiple],
    ebitda,
    keyManRisk,
  }
}

function generateValueUnlocks(
  data: QuizData,
  keyManRisk: 'High' | 'Moderate' | 'Low',
  qualityScore: number,
): string[] {
  const unlocks: string[] = []

  // Key man risk unlock (PRIORITY)
  if (keyManRisk === 'High') {
    unlocks.push(
      'Reduce your clinical hours and train associates to add $200K–$500K in value',
    )
  } else if (keyManRisk === 'Moderate') {
    unlocks.push(
      'Delegate more patient care to associates to increase your multiple by 0.5x',
    )
  }

  // Concentration fix
  if (data.concentration === 'yes') {
    unlocks.push('Reduce single-source risk to unlock a higher multiple')
  }

  // Margin lift
  if (['0-10', '10-15'].includes(data.profit)) {
    unlocks.push('A 5-point margin improvement could add $150K–$400K in value')
  }

  // Growth unlock
  if (['flat', 'down'].includes(data.trend)) {
    unlocks.push('Show 12 months of growth to increase buyer confidence')
  }

  // Scale unlock
  if (data.locations === '1') {
    unlocks.push('Multi-location clinics command premium multiples (3.5x–4.5x)')
  }

  // Financial planning unlock
  unlocks.push(
    'Proper financial planning and documentation can increase multiples by 1.0x–1.5x',
  )

  // Return top 3
  return unlocks.slice(0, 3)
}
